/**
 * Vincent bug #42 (2026-05-07, repris 2026-05-08) — "Back arrow always returns to /dashboard".
 *
 * Approach v2: keep the in-app stack ONLY to detect "do we have any back history",
 * then delegate to native `navigate(-1)` which uses the browser's history API. This
 * mirrors what the native browser Back button does, so the experience is consistent.
 *
 * Why not pop the stack manually (v1)?
 *   - We were doing _write(newStack.slice(0, -1)) which double-popped, then the
 *     useEffect re-pushed on navigation, leading to surprising states when users
 *     navigated back/forth quickly (Vincent QA tracker still flagged it as broken).
 *   - The native History API already handles this correctly. We only need to know
 *     whether there's a *previous in-app entry* to decide between navigate(-1) and
 *     a fallback.
 */
import { useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const STORAGE_KEY = "knoria_nav_stack";
const MAX_DEPTH = 30;

const _read = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const _write = (arr) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(arr.slice(-MAX_DEPTH)));
  } catch {
    // sessionStorage full / disabled — best-effort, no-op.
  }
};

/** Mount once — pushes every in-app pathname into the session stack. */
export function NavHistoryTracker() {
  const location = useLocation();
  useEffect(() => {
    const stack = _read();
    const lastEntry = stack[stack.length - 1];
    const newPath = location.pathname + location.search;
    // Avoid pushing the same path twice (StrictMode, replace navigations, etc.)
    if (lastEntry !== newPath) {
      stack.push(newPath);
      _write(stack);
    }
  }, [location.pathname, location.search]);
  return null;
}

/**
 * Returns a stable `goBack` callback.
 *
 * Strategy:
 *   1. If the in-app stack has ≥2 entries, use native `navigate(-1)` which
 *      replays the browser History API — same UX as the native Back button.
 *      The NavHistoryTracker effect will sync the stack on the next render.
 *   2. Otherwise, fall back to the provided fallback (default /dashboard).
 */
export function useNavBack(fallback = "/dashboard") {
  const navigate = useNavigate();
  return useCallback(() => {
    const stack = _read();
    if (stack.length >= 2) {
      navigate(-1);
      return;
    }
    navigate(fallback);
  }, [navigate, fallback]);
}
